/**
 * "Checks not written" on the Edit Check Specification panel (docgen ECS,
 * 2026-09-30). The spec stores what it did not write, with the reason; the
 * panel lists all of it in plain words. Pure, so it is tested from functions/
 * (vitest.web.config.ts).
 *
 * Shapes mirror functions/src/models/editCheckTypes.ts: EditCheckRejected,
 * EditCheckFamilyGap, EditCheckExcludedForm, EditCheckFamily.
 */

export interface RejectedCheck {
  source: 'model' | 'coverage' | 'family' | string;
  name: string;
  crfForm: string;
  fieldName: string;
  category: string;
  checkType: string;
  rule: string;
  reasons: string[];
}

export interface FamilyGap {
  family: string;
  reason: string;
  /** Set by the generator; absent on gaps stored before 2026-09-30. */
  applicability?: 'not_applicable' | 'not_written' | string;
  /** The positive fact behind not_applicable, e.g. "Study Settings: no pregnancy testing". */
  notApplicableBasis?: string;
}

export interface ExcludedForm {
  crfFormId: string;
  form: string;
  reason: string;
}

export interface NotWrittenFields {
  rejectedChecks?: RejectedCheck[];
  rejectedCount?: number;
  familyGaps?: FamilyGap[];
  excludedForms?: ExcludedForm[];
}

/**
 * The rule-written safety and dosing checks, named in words. The Word export
 * uses functions/src/editChecks/familyLabels.ts; the web build cannot import
 * it, so notWrittenDisplay.test.ts fails if the two tables differ.
 */
export const FAMILY_LABELS: Record<string, string> = {
  sae_timeliness: 'Serious adverse event reported on time',
  serious_ae_has_sae: 'Serious adverse event has a serious adverse event report',
  pregnancy_before_dose: 'Negative pregnancy test before the first dose',
  dlt_window: 'Dose-limiting toxicity within its window',
  sample_vs_dose_time: 'Pre-dose sample taken before the dose',
  death_consistency: 'Date of death agrees with adverse events and disposition',
  dose_vs_plan: 'Dose given matches the protocol',
  route_vs_protocol: 'Route given matches the protocol',
  prohibited_cm: 'No prohibited concomitant medication',
  value_plausibility: 'Value within a plausible range',
};

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

const sentence = (s: string) => {
  const t = String(s ?? '').trim();
  if (!t) return t;
  const c = t.charAt(0).toUpperCase() + t.slice(1);
  return /[.!?]$/.test(c) ? c : `${c}.`;
};

const words = (code: string) => {
  const t = String(code ?? '').replace(/[_-]+/g, ' ').trim().toLowerCase();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
};

export function familyLabel(family: string): string {
  return FAMILY_LABELS[family] ?? words(family);
}

/**
 * A rule-based check that does not apply to this study, as opposed to one
 * that applies but could not be written (docgen reasons, 2026-09-30). The
 * generator (functions/src/editChecks/safetyFamilies.ts, gapFor) stores
 * `applicability` on every gap. This reads that field and never the reason
 * sentence. Corrected 2026-10-01 (checker B1): not applicable needs a POSITIVE
 * fact, named in notApplicableBasis; a gap marked without one, or stored
 * before the field existed, is not written. Same rule as
 * functions/src/editChecks/familyLabels.ts.
 */
export function isNotApplicableGap(g: FamilyGap): boolean {
  return g?.applicability === 'not_applicable' && String(g.notApplicableBasis ?? '').trim() !== '';
}

export function splitFamilyGaps(gaps: readonly FamilyGap[] | undefined): {
  notWritten: FamilyGap[];
  notApplicable: FamilyGap[];
} {
  const notWritten: FamilyGap[] = [];
  const notApplicable: FamilyGap[] = [];
  for (const g of gaps ?? []) (isNotApplicableGap(g) ? notApplicable : notWritten).push(g);
  return { notWritten, notApplicable };
}

export function notWrittenSummary(spec: NotWrittenFields): {
  hasAny: boolean;
  total: number;
  headline: string;
  /**
   * True when nothing is missing and only not-applicable checks are listed:
   * the section is headed "Not applicable to this study (N)", never
   * "Checks not written (0)", and the panel does not repeat that heading.
   */
  onlyNotApplicable: boolean;
  rejected?: string;
  rejectedCut?: string;
  gaps?: string;
  notApplicable?: string;
  forms?: string;
} {
  const rejectedList = spec.rejectedChecks ?? [];
  const rejectedTotal = Math.max(spec.rejectedCount ?? 0, rejectedList.length);
  const { notWritten: gaps, notApplicable } = splitFamilyGaps(spec.familyGaps);
  const forms = spec.excludedForms ?? [];
  const total = rejectedTotal + gaps.length + forms.length;
  const onlyNotApplicable = total === 0 && notApplicable.length > 0;
  const out: ReturnType<typeof notWrittenSummary> = {
    hasAny: total + notApplicable.length > 0,
    total,
    headline: onlyNotApplicable ? `Not applicable to this study (${notApplicable.length})` : `Checks not written (${total})`,
    onlyNotApplicable,
  };
  if (rejectedTotal > 0) {
    out.rejected = rejectedTotal === 1
      ? '1 proposed check was not written because it did not match the data dictionary or the forms.'
      : `${rejectedTotal} proposed checks were not written because they did not match the data dictionary or the forms.`;
    if (rejectedList.length < rejectedTotal) {
      out.rejectedCut = `Showing the first ${rejectedList.length} of ${rejectedTotal}.`;
    }
  }
  if (gaps.length > 0) {
    out.gaps = `${gaps.length} rule-based ${plural(gaps.length, 'check', 'checks')} could not be written.`;
  }
  if (notApplicable.length > 0) {
    out.notApplicable = `${notApplicable.length} rule-based ${plural(notApplicable.length, 'check does', 'checks do')} not apply to this study.`;
  }
  if (forms.length > 0) {
    out.forms = `${forms.length} ${plural(forms.length, 'form gets', 'forms get')} no checks.`;
  }
  return out;
}

export function rejectedCheckLine(r: RejectedCheck): { title: string; reasons: string[] } {
  const text = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  const rule = text(r.rule) || text(r.name);
  const where = [text(r.crfForm), text(r.fieldName)].filter(Boolean).join(' · ');
  const what = rule ? sentence(rule) : 'A proposed check';
  const reasons = (Array.isArray(r.reasons) ? r.reasons : []).filter((x) => typeof x === 'string').map(sentence).filter(Boolean);
  return {
    title: where ? `${where}: ${what}` : what,
    reasons: reasons.length ? reasons : ['No reason was recorded.'],
  };
}

export function familyGapLine(g: FamilyGap): string {
  // A not-applicable check names the fact that says so, as the Word export does.
  const why = isNotApplicableGap(g) ? String(g.notApplicableBasis) : g.reason;
  return `${familyLabel(g.family)}: ${sentence(why) || 'No reason was recorded.'}`;
}

export function excludedFormLine(f: ExcludedForm): string {
  return `${f.form}: ${sentence(f.reason) || 'No reason was recorded.'}`;
}

type MaybeSourced = { source?: string };

export function isWrittenByRule(check: MaybeSourced): boolean {
  return check?.source === 'family';
}

/** The small label in the table's type column; undefined for generated checks. */
export function checkSourceBadge(check: MaybeSourced): string | undefined {
  if (check?.source === 'default_range' || (check as { rangeSource?: string })?.rangeSource === 'default') return 'Default range';
  if (check?.source === 'dictionary') return 'Dictionary';
  if (check?.source === 'family') return 'Written by rule';
  return undefined;
}
